import { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import analyticsApi, { FinancialHealthMetrics, FinancialRatio } from '../api/analytics';

interface FinancialHealthMetricsProps {
  className?: string;
}

export default function FinancialHealthMetricsComponent({ className = '' }: FinancialHealthMetricsProps) {
  const [healthMetrics, setHealthMetrics] = useState<FinancialHealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHealthMetrics();
  }, []);

  const loadHealthMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const metrics = await analyticsApi.getFinancialHealthMetrics();
      setHealthMetrics(metrics);
    } catch (err) {
      console.error('Error loading financial health metrics:', err);
      setError('Failed to load financial health metrics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'critical':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ChartBarIcon className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-slate-600 bg-slate-50/80 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white overflow-hidden shadow rounded-lg ${className}`}>
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Financial Health Metrics
          </h3>
          <div className="animate-pulse">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !healthMetrics) {
    return (
      <div className={`bg-white overflow-hidden shadow rounded-lg ${className}`}>
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Financial Health Metrics
          </h3>
          <div className="text-center py-4">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <p className="mt-2 text-sm text-red-600">
              {error || 'Failed to load metrics'}
            </p>
            <button
              onClick={loadHealthMetrics}
              className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white overflow-hidden shadow rounded-lg ${className}`}>
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
          Financial Health Metrics
        </h3>
        
        {/* Key Metrics Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Profit Margin</p>
            <p className="text-2xl font-semibold text-slate-900">
              {healthMetrics.profitMargin.toFixed(1)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Quick Ratio</p>
            <p className="text-2xl font-semibold text-slate-900">
              {healthMetrics.quickRatio.toFixed(2)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">Cash Ratio</p>
            <p className="text-2xl font-semibold text-slate-900">
              {healthMetrics.cashRatio.toFixed(2)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-500">ROI</p>
            <p className="text-2xl font-semibold text-slate-900">
              {healthMetrics.returnOnInvestment.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Financial Ratios */}
        <div>
          <h4 className="text-md font-medium text-slate-900 mb-3">Financial Ratios</h4>
          <div className="space-y-3">
            {healthMetrics.ratios.map((ratio: FinancialRatio, index: number) => (
              <div key={index} className={`p-3 rounded-lg border ${getStatusColor(ratio.status)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(ratio.status)}
                    <div className="ml-3">
                      <p className="text-sm font-medium">{ratio.name}</p>
                      <p className="text-xs text-slate-500">
                        Benchmark: {ratio.benchmark}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      {ratio.value.toFixed(2)}
                    </p>
                    <div className="flex items-center">
                      {ratio.value > ratio.benchmark ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
                      )}
                      <span className="text-xs capitalize">{ratio.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">Overall Health Score</span>
            <div className="flex items-center">
              {(() => {
                const goodRatios = healthMetrics.ratios.filter(r => r.status === 'good').length;
                const totalRatios = healthMetrics.ratios.length;
                const score = totalRatios > 0 ? (goodRatios / totalRatios) * 100 : 0;
                const scoreStatus = score >= 75 ? 'good' : score >= 50 ? 'warning' : 'critical';
                
                return (
                  <>
                    {getStatusIcon(scoreStatus)}
                    <span className={`ml-2 text-sm font-semibold ${
                      scoreStatus === 'good' ? 'text-green-600' :
                      scoreStatus === 'warning' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {score.toFixed(0)}%
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 